import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { getMyProfile } from '@/lib/api/profiles';
import { useAuth } from '@/lib/hooks/useAuth';

export function Me() {
  const { isAuthenticated, loaded } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['my-profile'],
    queryFn: getMyProfile,
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!loaded) return;
    if (!isAuthenticated) {
      navigate('/signin', { replace: true });
      return;
    }
    if (isLoading) return;

    // If the user arrived here via a protected redirect that remembered a `from`
    // location (e.g. /profile/:handle/claim), forward them there once the profile
    // is ready — unless it's a /claim path and they still have no handle (setup
    // isn't done yet).
    const from = (location.state as { from?: { pathname: string } } | null)?.from;
    const safePath = from?.pathname;
    if (safePath && safePath.startsWith('/') && !safePath.startsWith('//')) {
      const isClaimPath = safePath.includes('/claim');
      if (!isClaimPath || profile?.handle) {
        navigate(safePath, { replace: true });
        return;
      }
    }

    if (profile?.handle) {
      navigate(`/profile/${profile.handle}`, { replace: true });
    } else {
      navigate('/me/edit', { replace: true });
    }
  }, [loaded, isAuthenticated, isLoading, profile, navigate, location.state]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream">
      <Skeleton className="h-12 w-48" />
    </div>
  );
}
